import type { WavesEngineInput } from '@shared/engine/waves/core';

/**
 * Waves-mode input source contract. Mirrors Pulse's
 * {@link import('../input/inputSource.js').InputSource} but emits the
 * mania `down`/`up` lexicon — Waves uses the mania reuse pattern at
 * the replay level even though it's a one-button mode (no lane).
 */
export interface WavesInputSource {
  readonly kind: 'keyboard' | 'replay' | 'pointer';
  /**
   * Drain captured input events queued since the last poll. Events
   * with `beat > currentBeat` (e.g. queued by a fast keypress mid-rAF-
   * stall) are left in the queue so the next tick can replay them at
   * their original beat — clamping a future event backwards would
   * award the player a press they hadn't actually made yet.
   */
  poll: (currentBeat: number) => WavesEngineInput[];
  /** Disconnect listeners; idempotent. */
  destroy: () => void;
  /**
   * Whether the input is currently held — used by the renderer to
   * paint a "boost" trail behind the player while ascending and by
   * the HUD to show the player's input state. Replay sources reflect
   * the last seen edge.
   */
  isPressing: () => boolean;
  /**
   * Prepare the source for a new loop. Drops any inputs still pending
   * from the loop that just ended (their beats live in the previous
   * loop's audio-clock space and would corrupt the new loop), then —
   * if the input is still physically held — queues a synthetic `down`
   * at `beat` so the reset engine matches what the player is doing.
   * No-op for replay sources, which already encode every edge in the
   * captured stream.
   */
  rehomeForLoopStart: (beat: number) => void;
}

/**
 * Live keyboard + pointer input source for Waves mode. Same
 * key/pointer surface as Pulse (Space / W / ArrowUp / J / left+right
 * click) so muscle memory transfers between modes. The single button
 * emits `down` on press and `up` on release; the engine integrates a
 * piecewise-linear vertical trajectory between edges.
 *
 * Inputs are captured continuously even while the audio clock hasn't
 * started ticking (lead-in), so the player can press in anticipation
 * without missing the first edge — the session clamps their beat to
 * `>= 0` before feeding the engine.
 */
export class KeyboardWavesInputSource implements WavesInputSource {
  readonly kind = 'keyboard' as const;
  private queue: WavesEngineInput[] = [];
  private getBeatAtNow: () => number;
  private isClockRunning: () => boolean;
  private heldKeys = new Set<string>();
  private activePointerIds = new Set<number>();
  private active = true;
  private pressing = false;

  constructor(
    getBeatAtNow: () => number,
    isClockRunning: () => boolean = () => true,
  ) {
    this.getBeatAtNow = getBeatAtNow;
    this.isClockRunning = isClockRunning;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('blur', this.onBlur);
  }

  /**
   * Matches the Pulse flip keys + Space / W / ArrowUp / J for keyboard
   * users. Same physical inputs across both modes so a Pulse player
   * doesn't have to learn a new key for Waves.
   */
  private isWaveKey(code: string, key: string): boolean {
    return (
      code === 'Space' ||
      code === 'KeyW' ||
      code === 'ArrowUp' ||
      code === 'KeyJ' ||
      key === ' '
    );
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.repeat) {
      return;
    }
    if (!this.isWaveKey(e.code, e.key)) {
      return;
    }
    if (this.heldKeys.has(e.code)) {
      return;
    }
    this.heldKeys.add(e.code);
    e.preventDefault();
    this.emitEdge('down');
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.isWaveKey(e.code, e.key)) {
      return;
    }
    if (!this.heldKeys.delete(e.code)) {
      return;
    }
    // Only emit a release edge when the LAST held wave key was
    // released — overlapping presses (Space + J) should keep the
    // player pressing until both keys are up.
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 0) {
      return;
    }
    this.emitEdge('up');
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) {
      return;
    }
    const target = e.target as HTMLElement | null;
    if (target && (target.closest('button') || target.closest('input') || target.closest('a'))) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button === 2 && !target?.closest('canvas')) {
      return;
    }
    if (this.activePointerIds.has(e.pointerId)) {
      return;
    }
    this.activePointerIds.add(e.pointerId);
    // Only fire `down` on the first contact — overlapping pointers
    // should keep the player pressing until ALL pointers lift.
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 1) {
      return;
    }
    this.emitEdge('down');
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.activePointerIds.delete(e.pointerId)) {
      return;
    }
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 0) {
      return;
    }
    this.emitEdge('up');
  };

  private onPointerCancel = (e: PointerEvent): void => {
    this.activePointerIds.delete(e.pointerId);
    if (this.heldKeys.size === 0 && this.activePointerIds.size === 0 && this.pressing) {
      this.emitEdge('up');
    }
  };

  private onContextMenu = (e: MouseEvent): void => {
    if (!this.active || !this.isClockRunning()) {
      return;
    }
    const target = e.target as HTMLElement | null;
    if (!target?.closest('canvas')) {
      return;
    }
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    e.preventDefault();
  };

  private onBlur = (): void => {
    this.heldKeys.clear();
    this.activePointerIds.clear();
    if (this.pressing) {
      this.emitEdge('up');
    }
  };

  private emitEdge(type: 'down' | 'up'): void {
    // Track pressing state regardless of the clock so the renderer
    // can react to inputs during lead-in; the engine doesn't see
    // sub-zero beats, but the visible "boost" effect should still
    // light up the moment the player presses.
    this.pressing = type === 'down';
    if (!this.isClockRunning()) {
      return;
    }
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type });
  }

  poll(currentBeat: number): WavesEngineInput[] {
    if (this.queue.length === 0) {
      return [];
    }
    // Tail-trim events queued past the engine's processing window.
    // A rAF stall can let an input land at a beat ahead of the
    // session's `cappedTarget`; we keep those events in the queue so
    // the next tick can serve them at their real beat rather than
    // pulling them back into the past.
    const ready: WavesEngineInput[] = [];
    const deferred: WavesEngineInput[] = [];
    for (const inp of this.queue) {
      if (inp.beat <= currentBeat) {
        ready.push(inp);
      } else {
        deferred.push(inp);
      }
    }
    this.queue = deferred;
    return ready;
  }

  isPressing(): boolean {
    return this.pressing;
  }

  /**
   * Prepare the queue for a new loop's audio clock.
   *
   * A loop restart resets the audio clock to 0, so any inputs still
   * pending from the loop that just ended carry beats in the OLD loop's
   * coordinate space (typically just past the chart end — a press the
   * player mashed through the loop transition that the engine completed
   * before serving). Replaying them against the new loop reorders the
   * captured stream and desyncs the live run from the server's per-loop
   * re-simulation, so drop them outright.
   *
   * Then, if the player is still physically holding a key/pointer, queue
   * a synthetic `down` at `beat`: the per-loop reset zeroed
   * `state.pressing` but the OS fires no fresh keydown for the held key,
   * so without this the still-held input produces no engine response
   * until the player releases and re-presses. The clock-running guard
   * `emitEdge` uses does NOT apply here — this is a deliberate loop-start
   * re-press, and the new loop's first tick will serve it.
   */
  rehomeForLoopStart(beat: number): void {
    this.queue.length = 0;
    if (this.pressing) {
      this.queue.push({ beat, type: 'down' });
    }
  }

  destroy(): void {
    this.active = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('blur', this.onBlur);
  }
}

/**
 * Replay-source variant of {@link KeyboardWavesInputSource}. Replays
 * a captured input stream into the engine in beat order — the headless
 * server-side runner uses the same input shape via
 * {@link import('@shared/engine/waves/headlessRunner.js').runWavesReplay},
 * so a replay run on the client and server emit identical engine
 * traces.
 */
export class ReplayWavesInputSource implements WavesInputSource {
  readonly kind = 'replay' as const;
  private inputs: WavesEngineInput[];
  private cursor = 0;
  private pressing = false;

  constructor(inputs: WavesEngineInput[]) {
    this.inputs = [...inputs].sort((a, b) => a.beat - b.beat);
  }

  poll(currentBeat: number): WavesEngineInput[] {
    const out: WavesEngineInput[] = [];
    while (this.cursor < this.inputs.length && this.inputs[this.cursor].beat <= currentBeat) {
      const inp = this.inputs[this.cursor];
      out.push(inp);
      this.pressing = inp.type === 'down';
      this.cursor += 1;
    }
    return out;
  }

  isPressing(): boolean {
    return this.pressing;
  }

  rehomeForLoopStart(_beat: number): void {
    // Replay inputs are pre-captured; any required `down` at the loop
    // boundary is already part of the replay stream.
  }

  destroy(): void {
    this.inputs = [];
    this.cursor = 0;
  }

  isExhausted(): boolean {
    return this.cursor >= this.inputs.length;
  }
}
